import type { ProductLayoutNode, ProductLayoutFieldNode } from 'models/forms/FormResult';
import { BlockMetadata } from 'models/metadata/block.definition';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { createSelector } from 'reselect';
import { QuoteFormError } from 'state/addonForm/addonForm.selector';
import {
  essentialFormInfoProductSubtype,
  essentialFormInfoProductType,
  selectEssentialFormInfos,
  selectEssentialFormInfosErrors,
  selectEssentialsFormQuoteId,
  selectEssentialValidationFormErrors,
} from 'state/essential/infos/selectors';
import { isFormInputData, isPendingEnrichment } from 'state/formData/formData.selectors';
import { getRejections } from 'state/forms/rejections/selectors';
import { selectProductDescription } from 'state/product-description/selectors';
import { Width } from 'state/product-description/types';
import {
  getFormProductVariation,
  selectEssentialProductVariations,
} from 'state/productsReferential/productsReferential.selectors';
import { selectQuoteResult } from 'state/quotes/results/results.selectors';
import { isPastQuote } from 'state/quotes/utils';
import { getQuoteRejectionsByQuoteId } from 'state/selectors/common';
import { AppState } from 'state/state.model';
import {
  getUserPreferenceProductLayoutOverride,
  getUserPreferencesPinnedProductsVariations,
  selectUserPreferencesEssentialExpandedBlocks,
} from 'state/userPreferences/userPreferences.selectors';
import { selectEssentialWorkspaceTabProductVariation } from 'state/workspaces/essential/selectors';
import { getTabIsFrozen, getWorkspaceTab } from 'state/workspaces/tabs/tabs.selector';
import { isEqual } from 'utils/productVariation';

import { EssentialBlockNode, EssentialFieldNode, EssentialFormNode, EssentialWorkflowStatus } from './model';
import {
  containsAdvancedFieldInUserPreferences,
  applyCustomLayoutPreferences,
  getValidationOfProductLayoutNode,
  isProductLayoutBlockNode,
} from './selectors.shared';

export const selectEssentialFormNodes = createSelector(
  [
    (state, formId) => selectEssentialProductLayout(state, formId),
    (state, formId) =>
      getUserPreferenceProductLayoutOverride(state, selectEssentialWorkspaceTabProductVariation(state, formId)),
    selectEssentialProducts,
    selectEssentialExpandedBlockIds,
    selectEssentialFormInfosErrors,
    (_state, _tabId, currentSectionId) => currentSectionId,
    (_state, _tabId, _currentSectionId, isLayoutEditing) => isLayoutEditing,
  ],
  (
    productLayout,
    customProductLayoutFromUserPrefs,
    products,
    expandedBlockIds,
    validationErrors,
    _currentSectionId,
    isLayoutEditing,
  ): EssentialFormNode[] => {
    if (!products || !productLayout) {
      return emptyNodesArray;
    }

    const hasCustomVisibilityPreferences = customProductLayoutFromUserPrefs !== undefined;

    const visibleConfigFields = hasCustomVisibilityPreferences
      ? new Set(customProductLayoutFromUserPrefs.visibleFields)
      : undefined;

    const additionalConfigFields = hasCustomVisibilityPreferences
      ? new Set(customProductLayoutFromUserPrefs.additionalFields)
      : undefined;

    const fieldOverrides = hasCustomVisibilityPreferences
      ? (customProductLayoutFromUserPrefs.fieldOverrides ?? {})
      : undefined;

    const customizedLayout = applyCustomLayoutPreferences(productLayout, customProductLayoutFromUserPrefs);
    const expandAllBlocks = isLayoutEditing;

    return customizedLayout.nodes.map((node: ProductLayoutNode) =>
      convertProductLayoutNodeToEssentialFormNode(
        node,
        expandedBlockIds,
        validationErrors ?? [],
        undefined,
        visibleConfigFields,
        additionalConfigFields,
        fieldOverrides,
        expandAllBlocks,
      ),
    );
  },
);

function convertProductLayoutNodeToEssentialFormNode(
  node: ProductLayoutNode,
  expandedBlockIds: string[],
  validationErrors: QuoteFormError[],
  isBlockExpanded: boolean | undefined,
  visibleConfigFields: Set<string> | undefined,
  additionalConfigFields: Set<string> | undefined,
  fieldOverrides: Record<string, Width> | undefined,
  expandAllBlocks: boolean,
): EssentialFormNode {
  if (isProductLayoutBlockNode(node)) {
    const blockDef = node.definition;
    const hasAdvancedField = containsAdvancedFieldInUserPreferences(node, additionalConfigFields);
    const type = hasAdvancedField ? 'Expandable' : (blockDef.type as BlockMetadata['type']);
    const isExpanded = type === 'Expandable' ? expandAllBlocks || expandedBlockIds.includes(node.id) : undefined;

    const nodes = node.childrens.map(child =>
      convertProductLayoutNodeToEssentialFormNode(
        child,
        expandedBlockIds,
        validationErrors,
        isExpanded,
        visibleConfigFields,
        additionalConfigFields,
        fieldOverrides,
        expandAllBlocks,
      ),
    );

    const errors = getValidationOfProductLayoutNode(node.id, node.childrens, validationErrors);

    return {
      ...blockDef,
      width: node.width ?? blockDef.width,
      nodes,
      type,
      isExpanded,
      errors,
      warnings: [],
    } as EssentialBlockNode;
  }

  const fieldDef = (node as ProductLayoutFieldNode).definition;
  const hasCustomVisibilityPreference =
    (visibleConfigFields?.has(node.id) ?? false) || (additionalConfigFields?.has(node.id) ?? false);

  const isForAdvancedMode = hasCustomVisibilityPreference
    ? (additionalConfigFields?.has(node.id) ?? false)
    : fieldDef.isForAdvancedMode;

  const isHidden = isBlockExpanded === false && isForAdvancedMode ? true : fieldDef.isHidden;

  return {
    ...fieldDef,
    width: fieldOverrides?.[node.id] ?? node.width ?? fieldDef.width,
    isForAdvancedMode,
    isHidden,
  } as EssentialFieldNode;
}

export const selectEssentialsFormQuoteResult = (state: AppState, formId: string) => {
  const quoteId = selectEssentialsFormQuoteId(state, formId);
  return selectQuoteResult(state, quoteId);
};

function selectEssentialProducts(state: AppState, tabId: string) {
  const productType = essentialFormInfoProductType(state, tabId);
  const productSubType = essentialFormInfoProductSubtype(state, tabId);
  return selectProductDescription(state.productDescription, 'Essential', productSubType, productType);
}

function selectEssentialExpandedBlockIds(state: AppState, tabId: string) {
  const productType = essentialFormInfoProductType(state, tabId);
  const productSubType = essentialFormInfoProductSubtype(state, tabId);
  return selectUserPreferencesEssentialExpandedBlocks(state, productType, productSubType);
}

const emptyNodesArray: EssentialFormNode[] = [];

function selectEssentialProductLayout(state: AppState, formId: string) {
  return state.workspaces.essential?.[formId]?.productLayout;
}

function selectEssentialQuoteRequestStartingTimeInMillis(state: AppState, formId: string) {
  const quoteResult = selectEssentialsFormQuoteResult(state, formId);
  const quotePending = state.quotes.pendings[formId];
  return quoteResult?.estimatedQuotingDurationInMillis ?? quotePending?.quoteRequestStartingTimeInMillis;
}

export const selectEssentialWorkflowStatus = createSelector(
  [
    (state: AppState, formId: string) => selectEssentialValidationFormErrors(state, formId),
    (state: AppState, formId: string) => state.workspaces.essential[formId],
    (state: AppState, formId: string) => selectEssentialQuoteRequestStartingTimeInMillis(state, formId),
    (state: AppState, formId: string) => selectEssentialsFormQuoteResult(state, formId),
    (state: AppState, formId: string) => selectEssentialFormInfos(state, formId),
  ],
  (formErrors, activeForm, startingTimeInMillis, quoteResult, essentialFormInfo): EssentialWorkflowStatus => {
    if (startingTimeInMillis) {
      return 'price-in-progress';
    }

    if (quoteResult?.status === 'Failed' || quoteResult?.status === 'Rejected') {
      return 'price-rejected';
    }

    if (isPastQuote(essentialFormInfo?.quoteState)) {
      return 'price-expired';
    }

    if (quoteResult?.status === 'Quoted') {
      return 'priced';
    }

    if (activeForm?.isProductValidated !== true) {
      return 'creating';
    }

    const hasFormErrors = Boolean(formErrors?.length);
    if (hasFormErrors) {
      return 'not-ready';
    }

    return 'ready';
  },
);

const emptyRejectionsArray: NextWorkflowError[] = [];

export function selectEssentialQuoteRejection(state: AppState, formId: string) {
  const quoteId = selectEssentialsFormQuoteId(state, formId);
  const requestType = getFormProductVariation(state, 'essential', formId)?.requestType;

  if (requestType === 'RequestForStream') {
    return getRejections(state, formId);
  }

  return getQuoteRejectionsByQuoteId(state, quoteId) ?? emptyRejectionsArray;
}

export function selectIsEssentialFormDirty(state: AppState, formId: string) {
  return isPendingEnrichment(state, formId) || isFormInputData(state, formId);
}

export function selectIsEssentialCrossBrowserFrozen(state: AppState, formId: string) {
  const isCrossBrowserDirty = selectEssentialFormInfos(state, formId)?.crossBrowserDirty ?? false;
  const isTabFrozen = getTabIsFrozen(state, formId) ?? false;
  return isCrossBrowserDirty || isTabFrozen;
}

export const selectEssentialTabName = createSelector(
  [(state: AppState, formId: string) => getWorkspaceTab(state, formId)],
  workspaceTab => {
    if (!workspaceTab) {
      return;
    }

    return workspaceTab?.name !== undefined
      ? { name: workspaceTab.name, index: undefined }
      : { name: undefined, index: workspaceTab?.count };
  },
);

export const selectEssentialProductsWithPinned = createSelector(
  [selectEssentialProductVariations, getUserPreferencesPinnedProductsVariations('essential')],
  (products, pinnedProducts) => {
    return products.map(product => {
      if (pinnedProducts.some(pinnedProductVariation => isEqual(pinnedProductVariation, product))) {
        return {
          ...product,
          assetTypes: [...(product.assetTypes || []), 'Pinned'],
        };
      }

      return product;
    });
  },
);
